
// ABOUTME: Custom hook for handling file uploads to Supabase Storage
// ABOUTME: Provides secure file upload functionality with validation and performance monitoring

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from '@/components/ui/use-toast';
import { validateFileType, validateFileSize } from '@/utils/validation';
import { traceUIInteraction, captureError, logger } from '@/utils/sentry';

export interface FileUploadOptions {
  bucket: string;
  allowedTypes: string[];
  maxSizeInMB: number;
}

export function useFileUpload() {
  const [isUploading, setIsUploading] = useState(false);
  const { user } = useAuth();

  const uploadFile = async (
    file: File, 
    options: FileUploadOptions,
    customPath?: string
  ): Promise<string | null> => {
    if (!user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to upload files",
        variant: "destructive",
      });
      return null;
    }

    return traceUIInteraction(
      'File Upload',
      'upload',
      async () => {
        // Validate file type
        if (!validateFileType(file, options.allowedTypes)) {
          toast({
            title: "Invalid file type",
            description: `Please upload one of: ${options.allowedTypes.join(', ')}`,
            variant: "destructive",
          });
          return null;
        }

        // Validate file size
        if (!validateFileSize(file, options.maxSizeInMB)) {
          toast({
            title: "File too large",
            description: `Please upload a file smaller than ${options.maxSizeInMB}MB`,
            variant: "destructive",
          });
          return null;
        }

        setIsUploading(true);

        try {
          const fileExt = file.name.split('.').pop();
          const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = customPath || `${user.id}/${fileName}`;

          logger.info("Starting file upload", {
            fileName: file.name,
            fileSize: file.size,
            bucket: options.bucket,
            userId: user.id
          });

          const { error: uploadError } = await supabase.storage
            .from(options.bucket)
            .upload(filePath, file);

          if (uploadError) {
            throw uploadError;
          }

          // Get public URL
          const { data } = supabase.storage
            .from(options.bucket)
            .getPublicUrl(filePath);

          toast({
            title: "Upload successful",
            description: "File uploaded successfully",
          });

          logger.info("File upload completed", {
            fileName: file.name,
            filePath,
            publicUrl: data.publicUrl
          });

          return data.publicUrl;
        } catch (error) {
          logger.error("File upload failed", {
            fileName: file.name,
            bucket: options.bucket,
            error: error.message
          });
          
          captureError(error as Error, {
            operation: 'fileUpload',
            fileName: file.name,
            bucket: options.bucket,
            userId: user.id
          });
          
          toast({
            title: "Upload failed",
            description: "Failed to upload file. Please try again.",
            variant: "destructive",
          });
          return null;
        } finally {
          setIsUploading(false);
        }
      },
      {
        fileName: file.name,
        fileSize: file.size,
        bucket: options.bucket
      }
    );
  };

  return {
    uploadFile,
    isUploading,
  };
}
